import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db7n48f3hiaolq192jf0";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(isSpam ? "提交未通过校验，请稍后重试。" : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="feedback-form"
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="fb-name" className="text-sm text-foreground-600">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="fb-email" className="text-sm text-foreground-600">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="fb-type" className="text-sm text-foreground-600">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="feedback_type"
          required
          defaultValue="内容建议"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
        >
          <option value="内容建议">内容建议</option>
          <option value="功能反馈">功能反馈</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="fb-message" className="text-sm text-foreground-600">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或遇到的问题（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="jp-hp-field"
      />

      {status === "success" && (
        <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
          感谢你的反馈，我们已收到！
        </p>
      )}
      {status === "error" && (
        <p className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-700">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap sm:w-fit"
      >
        <i className="ri-send-plane-line" aria-hidden="true"></i>
        {status === "loading" ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}